/**
 * Bridges queue/ticket QueueRealtimeEvent WebSocket payloads into the unified
 * notification UI pipeline (toast + bell + unread) without a second socket.
 *
 * Dispatches `hd:queue-realtime-notification-ui` for NotificationContext to consume.
 */

import { isQueueRealtimePayload, isQueueRealtimeRelevantForCurrentUser } from './queueRealtimeSyncService';

const LOG_NS = 'queue-notification-bridge';

/** Backend sets metadata.skipBellSynthetic=true when persisted unassigned-create notifications own the bell. */
export function shouldSkipQueueBellSynthetic(msg) {
  if (!msg || typeof msg !== 'object') {
    return false;
  }
  const meta = msg.metadata;
  if (!meta || typeof meta !== 'object') {
    return false;
  }
  const flag = meta.skipBellSynthetic ?? meta.skip_bell_synthetic;
  return flag === true || flag === 'true' || flag === '1';
}

function logBridge(stage, fields = {}) {
  //console.log(
  //   JSON.stringify({
  //     ns: LOG_NS,
  //     stage,
  //     ts: new Date().toISOString(),
  //     ...fields,
  //   })
  // );
}

/**
 * Maps backend QueueRealtimeEvent.eventType to frontend notificationType used by
 * NotificationContext / toast allowlist (lowercase snake_case).
 */
export function mapQueueEventTypeToNotificationType(eventType) {
  if (!eventType || typeof eventType !== 'string') {
    return 'info';
  }
  const t = eventType.trim();
  switch (t) {
    case 'TICKET_CREATED':
      return 'ticket_created';
    case 'TICKET_TEAM_TRANSFERRED':
      return 'ticket_team_transferred';
    case 'TICKET_ASSIGNED':
      return 'ticket_assigned';
    case 'TICKET_REASSIGNED':
      return 'ticket_assigned';
    case 'TICKET_UNASSIGNED':
      return 'ticket_unassigned';
    case 'TICKET_REOPENED':
      return 'ticket_reopened';
    case 'TICKET_STATUS_CHANGED':
      return 'queue_transfer';
    default:
      return 'ticket_update';
  }
}

function formatTicketLabel(msg) {
  if (msg.ticketNumber != null && String(msg.ticketNumber).trim() !== '') {
    return String(msg.ticketNumber).trim();
  }
  if (msg.ticketId != null) {
    return `Ticket #${msg.ticketId}`;
  }
  return 'Ticket';
}

/**
 * Human-readable copy for synthetic bell/toast rows.
 */
export function buildQueueRealtimeNotificationCopy(msg) {
  const label = formatTicketLabel(msg);
  const trigger = msg.trigger != null && String(msg.trigger).trim() !== '' ? String(msg.trigger).trim() : null;

  switch (msg.eventType) {
    case 'TICKET_CREATED':
      return {
        title: 'New ticket',
        message: `${label} was created.`,
      };
    case 'TICKET_TEAM_TRANSFERRED':
      return {
        title: 'Ticket transferred',
        message: `${label} was moved between teams.`,
      };
    case 'TICKET_ASSIGNED':
      return {
        title: 'Ticket assigned to you',
        message: `${label} has been assigned to you.`,
      };
    case 'TICKET_REASSIGNED':
      return {
        title: 'Ticket reassigned to you',
        message: `${label} has been reassigned to you.`,
      };
    case 'TICKET_UNASSIGNED':
      return {
        title: 'Ticket unassigned',
        message: `${label} is no longer assigned.`,
      };
    case 'TICKET_REOPENED':
      return {
        title: 'Ticket reopened',
        message: `${label} was reopened.`,
      };
    case 'TICKET_STATUS_CHANGED':
      return {
        title: 'Ticket status updated',
        message: `${label} status changed.`,
      };
    default:
      return {
        title: 'Queue update',
        message: `${label} was updated.`,
      };
  }
}

/**
 * Build a synthetic notification object compatible with NotificationContext / addNotification.
 */
export function buildSyntheticNotificationFromQueueEvent(msg) {
  const eventId = msg.eventId != null && String(msg.eventId).trim() !== '' ? String(msg.eventId).trim() : null;
  const idKey = eventId != null ? `qr:${eventId}` : `qr:${msg.ticketId}:${msg.eventType}:${Date.now()}`;
  const notificationType = mapQueueEventTypeToNotificationType(msg.eventType);
  const { title, message } = buildQueueRealtimeNotificationCopy(msg);
  const now = new Date().toISOString();
  const ticketId = msg.ticketId != null ? Number(msg.ticketId) : null;
  const ticketNumber =
    msg.ticketNumber != null && String(msg.ticketNumber).trim() !== ''
      ? String(msg.ticketNumber).trim()
      : null;

  return {
    notificationId: idKey,
    id: idKey,
    notificationType,
    type: notificationType,
    action: 'created',
    title,
    message,
    body: message,
    ticketId,
    ticketNumber,
    link: ticketId != null ? `/agent/tickets/${ticketId}` : null,
    priority: 'normal',
    isRead: false,
    read: false,
    timestamp: now,
    createdAt: now,
    metadata: {
      source: 'queue_realtime',
      queueEventId: eventId,
      bridgeEventType: msg.eventType,
      ticketId,
      ticketNumber,
      tenantId: msg.tenantId,
      correlationId: msg.correlationId,
      traceId: msg.traceId,
      affectedTeamIds: msg.affectedTeamIds,
      destinationTeamId: msg.destinationTeamId,
      sourceTeamId: msg.sourceTeamId,
      destinationAssigneeId: msg.destinationAssigneeId ?? null,
      sourceAssigneeId: msg.sourceAssigneeId ?? null,
    },
    data: {
      source: 'queue_realtime',
      queueEventId: eventId,
      bridgeEventType: msg.eventType,
      ticketId,
      ticketNumber,
      destinationAssigneeId: msg.destinationAssigneeId ?? null,
    },
  };
}

const UI_EVENT_NAME = 'hd:queue-realtime-notification-ui';

/**
 * Validates relevance, builds synthetic notification, dispatches DOM event for NotificationContext.
 * @returns {boolean} true if an event was dispatched
 */
export function dispatchQueueRealtimeNotificationBridge(msg) {
  if (!isQueueRealtimePayload(msg)) {
    logBridge('bridge_skipped', { reason: 'not_queue_payload' });
    return false;
  }
  if (!isQueueRealtimeRelevantForCurrentUser(msg)) {
    logBridge('bridge_skipped', { reason: 'not_relevant_for_user', eventType: msg.eventType, ticketId: msg.ticketId });
    return false;
  }
  if (shouldSkipQueueBellSynthetic(msg)) {
    logBridge('bridge_skipped', {
      reason: 'persisted_notification_owner',
      eventType: msg.eventType,
      ticketId: msg.ticketId,
    });
    return false;
  }

  const synthetic = buildSyntheticNotificationFromQueueEvent(msg);
  logBridge('bridge_dispatch', {
    eventType: msg.eventType,
    ticketId: msg.ticketId,
    queueEventId: synthetic.metadata?.queueEventId,
    notificationType: synthetic.notificationType,
  });

  window.dispatchEvent(
    new CustomEvent(UI_EVENT_NAME, {
      detail: {
        source: 'queue_realtime_bridge',
        queueMessage: msg,
        notification: synthetic,
      },
    })
  );
  return true;
}

export const QUEUE_REALTIME_NOTIFICATION_UI_EVENT = UI_EVENT_NAME;
